import React from 'react';
import { SchemaObject, ParameterObject } from 'openapi3-ts';
import { SchemaObjectEntry } from '../index';
import SchemaRow from './SchemaRow';
import EmptySchemaRow from './EmptySchemaRow';

import styles from './Table.module.scss';
import { getSchemaDepth, hasAlternatives } from './schemaUtils';
import ChatbotFriendlyRow from './ChatbotFriendlyRow';
import OneOfOptions, { getOneOfAlternativeFieldNames } from './OneOfOptions';

interface Props {
  schemaName: string;
  entries: Array<SchemaObjectEntry>;
  pathEntries: Array<ParameterObject>;
  required?: Array<string>;
  expandAll: boolean;
  expandText?: string;
  hideText?: string;
  simplify?: boolean;
  unhideFlag?: string;
  type?: string;
  responseExamples?: SchemaObject['examples'];
  stringCode?: string;
  route?: string;
  exchangeSpec?: string;
  coreExchangeSpec?: string;
  headerHeight?: number;
  onSchemaRowLineNumbers?: (lineStart: number | null) => void;
  schema?: SchemaObject;
}

const Table: React.FC<Props> = (props: Props) => {
  const [topMarginVal, setTopMarginVal] = React.useState(96);

  const visibleEntries = props.entries.filter((entry: SchemaObjectEntry) => {
    return !(
      (entry[1].hasOwnProperty('x-hidden-from-docs') &&
        entry[1]['x-hidden-from-docs'] === true) ||
      (entry[1].hasOwnProperty('x-hidden-behind-flag') &&
        entry[1]['x-hidden-behind-flag'] !== props.unhideFlag)
    );
  });
  const maxTableDepth = Math.max(
    -1,
    ...visibleEntries.map((entry: SchemaObjectEntry) =>
      getSchemaDepth(entry[1]),
    ),
  );
  // Header params (for example Plaid-New-User-API-Enabled on /user/*) are not
  // request fields, so they stay out of the table even though the spec lists
  // them alongside path and query params.
  const pathEntries = (props.pathEntries || []).filter(
    (pathEntry) => pathEntry.in === 'path' || pathEntry.in === 'query',
  );
  const alternativeFieldNames = new Set(
    getOneOfAlternativeFieldNames(props.schema),
  );
  const sharedEntries = visibleEntries.filter(
    ([fieldName]) => !alternativeFieldNames.has(fieldName),
  );
  const alternativeEntries = visibleEntries.filter(([fieldName]) =>
    alternativeFieldNames.has(fieldName),
  );
  const alternativeGuidance = <OneOfOptions schema={props.schema} />;

  React.useEffect(() => {
    // Figure out the "top" of our scroll container. This should be 96,
    // but calculating it like this seemed better than hard-coding it.
    const container = document.querySelector('.docs-scroll-container');
    if (container == null) {
      return;
    }
    const topMargin = parseInt(getComputedStyle(container).marginTop);
    if (Number.isNaN(topMargin)) {
      return;
    }
    setTopMarginVal(topMargin);
  }, []);

  const renderSchemaEntry = (schemaEntry: SchemaObjectEntry) => {
    const key = schemaEntry[0];
    return props.simplify ? (
      <ChatbotFriendlyRow
        schemaName={props.schemaName}
        schemaEntry={schemaEntry}
        key={key}
        required={props.required && props.required.includes(key)}
        type={props.type}
        parentKeys={[]}
        route={props.route}
        exchangeSpec={props.exchangeSpec}
        coreExchangeSpec={props.coreExchangeSpec}
      />
    ) : (
      <SchemaRow
        schemaName={props.schemaName}
        depth={0}
        schemaEntry={schemaEntry}
        maxDepth={maxTableDepth}
        key={key}
        required={props.required && props.required.includes(key)}
        expandAll={props.expandAll}
        expandText={props.expandText}
        hideText={props.hideText}
        unhideFlag={props.unhideFlag}
        type={props.type}
        parentKeys={[]}
        responseExamples={props.responseExamples}
        stringCode={props.stringCode}
        route={props.route}
        exchangeSpec={props.exchangeSpec}
        coreExchangeSpec={props.coreExchangeSpec}
        topMargin={topMarginVal}
        parentHeight={props.headerHeight}
        onLineNumbers={props.onSchemaRowLineNumbers}
      />
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.schemaTable}>
        <div className={styles.body}>
          {visibleEntries.length === 0 &&
            pathEntries.length === 0 &&
            !hasAlternatives(props.schema) && <EmptySchemaRow />}
          {pathEntries.map((pathEntry, idx) => (
            <SchemaRow
              key={pathEntry.name || idx}
              schemaName={props.schemaName}
              depth={0}
              schemaEntry={['', {}]}
              pathEntry={pathEntry}
              maxDepth={maxTableDepth}
              expandAll={props.expandAll}
              expandText={props.expandText}
              hideText={props.hideText}
              unhideFlag={props.unhideFlag}
              type={props.type}
              parentKeys={[]}
              responseExamples={props.responseExamples}
              stringCode={props.stringCode}
              route={props.route}
              topMargin={topMarginVal}
              parentHeight={props.headerHeight}
              onLineNumbers={props.onSchemaRowLineNumbers}
            />
          ))}
          {/* The guidance introduces the fields only some alternatives accept;
              with none to introduce, it explains the object and leads. */}
          {alternativeEntries.length === 0 && alternativeGuidance}
          {sharedEntries.map(renderSchemaEntry)}
          {alternativeEntries.length > 0 && (
            <>
              {alternativeGuidance}
              {alternativeEntries.map(renderSchemaEntry)}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

Table.displayName = 'Table';

export default Table;
